import React, { useState, useCallback, type Dispatch, useEffect, useRef } from 'react';
import { styled } from 'baseui';
import { Input, SIZE } from 'baseui/input';
import { ButtonGroup } from 'baseui/button-group';
import { Button } from 'baseui/button';
import globalWindow, { ResizeObserver } from 'global';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, KEY_ESCAPE_COMMAND, SELECTION_CHANGE_COMMAND, type LexicalEditor, type BaseSelection } from 'lexical';
import { getSelectedNode, sanitizeUrl } from '../../utils/utils';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { setFloatingElemPosition } from './utils/utils';
import CircleCheckFilled from '@uber/icons/circle-check-filled';
import CircleXFilled from '@uber/icons/circle-x-filled';
import PencilFilled from '@uber/icons/pencil-filled';
import TrashCanFilled from '@uber/icons/trash-can-filled';
const ButtonGroupProps = {
  size: SIZE.mini,
  overrides: {
    Root: {
      style: () => ({
        gap: '5px'
      })
    }
  }
};
const LinkEditorContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  position: 'absolute',
  top: '0',
  left: '0',
  zIndex: '1',
  maxWidth: 'fit-content',
  width: '100%',
  opacity: '0',
  backgroundColor: $theme.colors.backgroundPrimary,
  boxShadow: `0 5px 10px rgba(0, 0, 0, 0.3)`,
  borderRadius: $theme.sizing.scale300,
  transition: 'opacity 0.5s',
  willChange: 'transform',
  justifyContent: 'space-between'
}));
const LinkEditorWrapper = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  padding: $theme.sizing.scale300
}));
const LinkViewContainer = styled('div', ({
  $theme
}) => ({
  marginRight: $theme.sizing.scale300,
  alignContent: 'center'
}));
const LinkEditor = ({
  editor,
  anchorElem,
  isLink,
  setIsLink,
  isLinkEditMode,
  setIsLinkEditMode
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [editedLinkUrl, setEditedLinkUrl] = useState<string>('https://');
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);
  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);
      if (linkParent) {
        setLinkUrl(linkParent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl);
      }
    }
    const editorElem = editorRef.current;
    // returns the Selection object representing the range of text selected by the user or the current position of the caret.
    const nativeSelection = globalWindow.getSelection();
    const activeElement = document.activeElement;
    if (editorElem === null) {
      return;
    }
    const rootElement = editor.getRootElement();
    if (selection !== null && nativeSelection !== null && rootElement !== null && rootElement.contains(nativeSelection.anchorNode) && editor.isEditable()) {
      const domRect = nativeSelection.focusNode?.parentElement?.getBoundingClientRect();
      if (domRect) {
        domRect.y += 40;
        setFloatingElemPosition(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.id !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPosition(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }
    return true;
  }, [anchorElem, editor, isLinkEditMode, linkUrl, setIsLinkEditMode]);
  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;
    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(document.body);
    if (scrollerElem) {
      resizeObserver.observe(scrollerElem);
    }
    return () => {
      resizeObserver.unobserve(document.body);
      if (scrollerElem) {
        resizeObserver.unobserve(scrollerElem);
      }
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);
  useEffect(() => {
    return mergeRegister(editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        updateLinkEditor();
      });
    }), editor.registerCommand(SELECTION_CHANGE_COMMAND, () => {
      updateLinkEditor();
      return true;
    }, COMMAND_PRIORITY_LOW), editor.registerCommand(KEY_ESCAPE_COMMAND, () => {
      if (isLink) {
        setIsLink(false);
        return true;
      }
      return false;
    }, COMMAND_PRIORITY_HIGH));
  }, [editor, isLink, setIsLink, updateLinkEditor]);
  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);
  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);
  const monitorInputInteraction = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };
  const handleLinkSubmission = () => {
    if (lastSelection === null) {
      return;
    }
    if (linkUrl !== '') {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl));
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }
        const parent = getSelectedNode(selection).getParent();
        if ($isAutoLinkNode(parent)) {
          const linkNode = $createLinkNode(parent.getURL(), {
            rel: parent.__rel,
            target: parent.__target,
            title: parent.__title
          });
          parent.replace(linkNode, true);
        }
      });
    }
    setEditedLinkUrl('https://');
    setIsLinkEditMode(false);
  };
  if (!isLink) {
    return <LinkEditorContainer ref={editorRef} />;
  }
  return <LinkEditorContainer ref={editorRef}>
      {isLinkEditMode ? <LinkEditorWrapper data-testid="link-edit-container">
          <LinkViewContainer>
            <Input inputRef={inputRef} value={editedLinkUrl} id="link-input" onChange={event => {
          setEditedLinkUrl(event.target.value);
        }} onKeyDown={event => {
          monitorInputInteraction((event as React.KeyboardEvent<HTMLInputElement>));
        }} size={SIZE.mini} />
          </LinkViewContainer>
          <ButtonGroup {...ButtonGroupProps}>
            <Button onClick={() => {
          setIsLinkEditMode(false);
        }}>
              <CircleXFilled />
            </Button>
            <Button onClick={handleLinkSubmission}>
              <CircleCheckFilled />
            </Button>
          </ButtonGroup>
        </LinkEditorWrapper> : <LinkEditorWrapper data-testid="link-view-container">
          <LinkViewContainer>
            <a href={sanitizeUrl(linkUrl)} target="_blank" rel="noopener noreferrer">
              {linkUrl}
            </a>
          </LinkViewContainer>
          <ButtonGroup {...ButtonGroupProps}>
            <Button onClick={() => {
          setEditedLinkUrl(linkUrl);
          setIsLinkEditMode(true);
        }}>
              <PencilFilled />
            </Button>
            <Button onClick={() => {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
        }}>
              <TrashCanFilled />
            </Button>
          </ButtonGroup>
        </LinkEditorWrapper>}
    </LinkEditorContainer>;
};
export default LinkEditor;